import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Guid from "_shared/guids/guid";
import MarketplaceTeam from "marketplace/teams/team";
import EntityMember from "_shared/entity-members/entity-member";
import { JSONType } from "work/proposals/redlining/field-redlines/field-redline";
import { VendorTeamJSON, VendorTeamParams } from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";

export default class VendorTeam {
  public static get EXAMPLE(): VendorTeam {
    return new VendorTeam({
      leader: EntityMember.EXAMPLE,
      members: [EntityMember.EXAMPLE],
      marketplaceId: Guid.generate(),
    });
  }

  public static fromJSON(json: VendorTeamJSON | null | undefined): VendorTeam | null | undefined {
    if (!json) return json;

    return new VendorTeam({
      leader: EntityMember.fromJSON(json.leader) ?? undefined,
      members:
        json.members?.map((m) => {
          const member = EntityMember.fromJSON(m);
          if (!member) throw new Error("Invalid member in VendorTeam JSON");
          return member;
        }) ?? [],
      marketplaceId: Guid.fromJSON(json.marketplaceId) ?? undefined,
    });
  }

  public static fromUnknownJSON(json: JSONType): VendorTeam | null | undefined {
    if (isNullOrUndefined(json)) return json;
    else if (isValidTeamJSON(json, { throw: true })) return VendorTeam.fromJSON(json);
    throw new Error("Cannot create VendorTeam from invalid JSON");
  }

  public readonly leader?: EntityMember;
  public readonly marketplaceId?: Guid;

  public get members(): EntityMember[] {
    return [...this._members];
  } // Return a copy to prevent external mutation

  private set members(members: EntityMember[]) {
    this._members = members;
  }

  public constructor(params: VendorTeamParams) {
    Object.assign(this, { ...params });
  }

  public static FromMarketplaceTeam(marketplaceTeam: MarketplaceTeam): VendorTeam | undefined {
    if (!marketplaceTeam) return undefined;
    return new VendorTeam({
      leader: marketplaceTeam.leader.toEntityMember(),
      members: marketplaceTeam.members.map((m) => m.toEntityMember()),
      marketplaceId: marketplaceTeam.id,
    });
  }

  public isEqualTo(other?: unknown): boolean {
    return (
      other instanceof VendorTeam &&
      ((!this.leader && !other.leader) || this.leader?.isEqualTo(other.leader) || false) &&
      this.members.length === other.members.length &&
      this.members.every((member) => other.members.some((otherMember) => member.isEqualTo(otherMember)))
    );
  }

  public toJSON(): VendorTeamJSON {
    return {
      leader: this.leader?.toJSON(),
      members: this._members.map((m) => m.toJSON()),
      marketplaceId: this.marketplaceId?.toJSON(),
    };
  }

  public clone(): VendorTeam {
    return new VendorTeam({
      leader: this.leader ? this.leader.clone() : undefined,
      members: this._members.map((m) => m.clone()),
      marketplaceId: this.marketplaceId ? this.marketplaceId.clone() : undefined,
    });
  }

  private _members: EntityMember[] = [];
}

export class InvalidVendorTeamJSON extends Error {
  public readonly json: JSONType;
  public readonly message: string = "Invalid VendorTeamJSON";

  constructor(args: { json: JSONType; message?: string }) {
    super(`${args.message}: ${JSON.stringify(args.json)}`);
    this.json = args.json;
  }
}

function isValidTeamJSON(json: JSONType, options?: { throw: boolean }): json is VendorTeamJSON {
  options = options ?? { throw: false };
  if (isNullOrUndefined(json)) return true;

  if (typeof json !== "object" || json === null) {
    if (options.throw) throw new InvalidVendorTeamJSON({ json });
    return false;
  }

  if ("leader" in json && !isNullOrUndefined(json.leader)) {
    if (typeof json.leader !== "object") {
      if (options.throw)
        throw new InvalidVendorTeamJSON({
          json,
          message: "Invalid VendorTeamJSON leader",
        });
      return false;
    }
  }

  if (!("members" in json) || !Array.isArray(json.members)) {
    if (options.throw)
      throw new InvalidVendorTeamJSON({
        json,
        message: "Invalid VendorTeamJSON members",
      });
    return false;
  }

  return true;
}
